import React from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Badge,
  Card,
  CardBody,
} from 'reactstrap';
import {
  FaExclamationTriangle,
  FaExclamationCircle,
  FaInfoCircle,
  FaCheckCircle,
  FaClock,
  FaUser,
  FaHashtag,
  FaCalendarAlt,
  FaTag,
  FaClipboardList,
} from 'react-icons/fa';

const getLevelIcon = (level) => {
  switch (level?.toLowerCase()) {
    case 'critica':
      return <FaExclamationCircle size={15} />;

    case 'advertencia':
      return <FaExclamationTriangle size={15} />;

    case 'informativa':
      return <FaInfoCircle size={15} />;

    default:
      return <FaInfoCircle size={15} />;
  }
};

const getLevelColor = (level) => {
  switch (level?.toLowerCase()) {
    case 'critica':
      return '#dc2626';

    case 'advertencia':
      return '#f59e0b';

    case 'informativa':
      return '#2563eb';

    default:
      return '#64748b';
  }
};

const getLevelBackground = (level) => {
  switch (level?.toLowerCase()) {
    case 'critica':
      return '#fff0f1';

    case 'advertencia':
      return '#fff8eb';

    case 'informativa':
      return '#eef6ff';

    default:
      return '#f1f5f9';
  }
};

const getStatusColor = (status) => {
  switch (status?.trim().toLowerCase()) {
    case 'nueva':
      return '#dc2626';

    case 'en seguimiento':
      return '#f59e0b';

    case 'resuelta':
      return '#16a34a';

    default:
      return '#64748b';
  }
};

const getStatusIcon = (status) => {
  switch (status?.trim().toLowerCase()) {
    case 'nueva':
      return <FaExclamationCircle size={12} />;

    case 'en seguimiento':
      return <FaClock size={12} />;

    case 'resuelta':
      return <FaCheckCircle size={12} />;

    default:
      return null;
  }
};

const InfoCard = ({
  icon,
  title,
  children,
  iconColor = '#2563eb',
  iconBackground = '#eef6ff',
}) => {
  return (
    <Card
      style={{
        height: '100%',
        border: '1px solid #e7ebf0',
        borderRadius: '7px',
        backgroundColor: '#ffffff',
        boxShadow: 'none',
      }}
    >
      <CardBody
        style={{
          padding: '13px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '9px',
          }}
        >
          <div
            style={{
              width: '30px',
              height: '30px',
              minWidth: '30px',
              borderRadius: '50%',
              backgroundColor: iconBackground,
              color: iconColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '9px',
            }}
          >
            {icon}
          </div>

          <span
            style={{
              color: '#475569',
              fontSize: '10px',
              fontWeight: '600',
            }}
          >
            {title}
          </span>
        </div>

        {children}
      </CardBody>
    </Card>
  );
};

const AlertDetailModal = ({
  isOpen,
  toggle,
  alert,
  onMarkAsResolved,
}) => {
  if (!isOpen || !alert) return null;

  const levelColor = getLevelColor(alert.level);
  const levelBackground = getLevelBackground(alert.level);
  const statusColor = getStatusColor(alert.status);

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="lg"
      backdrop="static"
      centered
      style={{
        maxWidth: '760px',
      }}
    >
      <ModalHeader
        toggle={toggle}
        style={{
          borderBottom: '1px solid #eef1f4',
          padding: '15px 18px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              width: '34px',
              height: '34px',
              minWidth: '34px',
              borderRadius: '50%',
              backgroundColor: levelBackground,
              color: levelColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '10px',
            }}
          >
            {getLevelIcon(alert.level)}
          </div>

          <div>
            <div
              style={{
                fontSize: '14px',
                fontWeight: '700',
                color: '#1f2937',
                lineHeight: '1.3',
              }}
            >
              Detalle de Alerta
            </div>

            <div
              style={{
                fontSize: '9px',
                color: '#64748b',
                marginTop: '2px',
              }}
            >
              Información y detalles de la alerta
            </div>
          </div>
        </div>
      </ModalHeader>

      <ModalBody
        style={{
          padding: '18px',
          backgroundColor: '#fafbfc',
        }}
      >
        <div
          style={{
            backgroundColor: '#ffffff',
            border: '1px solid #e7ebf0',
            borderRadius: '7px',
            padding: '14px',
            marginBottom: '12px',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: '12px',
            }}
          >
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: '14px',
                  fontWeight: '700',
                  color: '#1f2937',
                  lineHeight: '1.4',
                  marginBottom: '4px',
                }}
              >
                {alert.title}
              </div>

              <div
                style={{
                  color: '#94a3b8',
                  fontSize: '9px',
                }}
              >
                ID: {alert.id}
              </div>
            </div>

            <Badge
              pill
              style={{
                backgroundColor: statusColor,
                color: '#ffffff',
                border: 'none',
                fontSize: '9px',
                fontWeight: '600',
                padding: '6px 10px',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                whiteSpace: 'nowrap',
              }}
            >
              {getStatusIcon(alert.status)}
              {alert.status}
            </Badge>
          </div>
        </div>

        <Row className="g-2 mb-2">
          <Col md="6">
            <InfoCard
              icon={<FaUser size={13} />}
              title="Distribuidor / Usuario"
            >
              <div
                style={{
                  color: '#273142',
                  fontSize: '11px',
                  fontWeight: '600',
                  lineHeight: '1.4',
                }}
              >
                {alert.user}
              </div>

              {alert.code && (
                <div
                  style={{
                    color: '#64748b',
                    fontSize: '9px',
                    marginTop: '3px',
                  }}
                >
                  {alert.code}
                </div>
              )}
            </InfoCard>
          </Col>

          <Col md="6">
            <InfoCard
              icon={<FaTag size={13} />}
              title="Nivel de Alerta"
              iconColor={levelColor}
              iconBackground={levelBackground}
            >
              <Badge
                pill
                style={{
                  backgroundColor: levelColor,
                  color: '#ffffff',
                  border: 'none',
                  fontSize: '9px',
                  fontWeight: '600',
                  padding: '6px 10px',
                }}
              >
                {alert.level}
              </Badge>
            </InfoCard>
          </Col>
        </Row>

        <Row className="g-2 mb-2">
          <Col md="6">
            <InfoCard
              icon={<FaCalendarAlt size={13} />}
              title="Fecha y Hora"
            >
              <div
                style={{
                  color: '#273142',
                  fontSize: '11px',
                  fontWeight: '600',
                }}
              >
                {alert.date}
              </div>

              <div
                style={{
                  color: '#64748b',
                  fontSize: '9px',
                  marginTop: '3px',
                }}
              >
                {alert.time}
              </div>
            </InfoCard>
          </Col>

          <Col md="6">
            <InfoCard
              icon={<FaHashtag size={13} />}
              title="Código"
            >
              <div
                style={{
                  display: 'inline-block',
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '5px',
                  padding: '5px 8px',
                  color: '#475569',
                  fontSize: '9px',
                  fontFamily: 'monospace',
                }}
              >
                {alert.code || 'N/A'}
              </div>
            </InfoCard>
          </Col>
        </Row>

        {alert.description && (
          <Card
            style={{
              border: '1px solid #e7ebf0',
              borderRadius: '7px',
              backgroundColor: '#ffffff',
              boxShadow: 'none',
              marginBottom: '8px',
            }}
          >
            <CardBody
              style={{
                padding: '13px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '8px',
                }}
              >
                <div
                  style={{
                    width: '30px',
                    height: '30px',
                    minWidth: '30px',
                    borderRadius: '50%',
                    backgroundColor: '#eef6ff',
                    color: '#2563eb',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '9px',
                  }}
                >
                  <FaClipboardList size={13} />
                </div>

                <span
                  style={{
                    color: '#475569',
                    fontSize: '10px',
                    fontWeight: '600',
                  }}
                >
                  Descripción
                </span>
              </div>

              <p
                style={{
                  color: '#475569',
                  fontSize: '10px',
                  lineHeight: '1.6',
                  margin: '0',
                }}
              >
                {alert.description}
              </p>
            </CardBody>
          </Card>
        )}

        {alert.metadata &&
          Object.keys(alert.metadata).length > 0 && (
            <Card
              style={{
                border: '1px solid #e7ebf0',
                borderRadius: '7px',
                backgroundColor: '#ffffff',
                boxShadow: 'none',
              }}
            >
              <CardBody
                style={{
                  padding: '13px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '8px',
                  }}
                >
                  <div
                    style={{
                      width: '30px',
                      height: '30px',
                      minWidth: '30px',
                      borderRadius: '50%',
                      backgroundColor: '#eef6ff',
                      color: '#2563eb',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '9px',
                    }}
                  >
                    <FaInfoCircle size={13} />
                  </div>

                  <span
                    style={{
                      color: '#475569',
                      fontSize: '10px',
                      fontWeight: '600',
                    }}
                  >
                    Información Adicional
                  </span>
                </div>

                <div
                  style={{
                    borderTop: '1px solid #eef1f4',
                  }}
                >
                  {Object.entries(alert.metadata).map(
                    ([key, value]) => (
                      <div
                        key={key}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: '15px',
                          padding: '7px 0',
                          borderBottom:
                            '1px solid #f1f3f5',
                        }}
                      >
                        <span
                          style={{
                            color: '#64748b',
                            fontSize: '9px',
                            fontWeight: '600',
                          }}
                        >
                          {key}
                        </span>

                        <span
                          style={{
                            color: '#273142',
                            fontSize: '9px',
                            textAlign: 'right',
                          }}
                        >
                          {String(value)}
                        </span>
                      </div>
                    )
                  )}
                </div>
              </CardBody>
            </Card>
          )}
      </ModalBody>

      <ModalFooter
        style={{
          borderTop: '1px solid #eef1f4',
          padding: '11px 18px',
          backgroundColor: '#ffffff',
        }}
      >
        <Button
          onClick={toggle}
          size="sm"
          style={{
            backgroundColor: '#ffffff',
            border: '1px solid #dfe4ea',
            color: '#475569',
            borderRadius: '5px',
            fontSize: '10px',
            fontWeight: '600',
            padding: '6px 13px',
          }}
        >
          Cerrar
        </Button>

        {alert.status?.toLowerCase() !== 'resuelta' &&
          onMarkAsResolved && (
            <Button
              size="sm"
              onClick={() => {
                onMarkAsResolved(alert.id);
                toggle();
              }}
              style={{
                backgroundColor: '#16a34a',
                border: '1px solid #16a34a',
                color: '#ffffff',
                borderRadius: '5px',
                fontSize: '10px',
                fontWeight: '600',
                padding: '6px 13px',
              }}
            >
              <FaCheckCircle
                className="me-1"
                size={11}
              />
              Marcar como resuelta
            </Button>
          )}
      </ModalFooter>
    </Modal>
  );
};

export default AlertDetailModal;